'use client';
import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import VSymbol from 'public/img/icon/v-symbol.svg';
import { getRelatedProducts, algoliaResultToProductCard } from '@/util/search';
import { ProductCardData } from '@/types/Product';
import ProductCardCarousel from '@/components/common/ProductCardCarousel/ProductCardCarousel';
import ProductCard from '@/components/common/ProductCard/ProductCard';
import Text from '@/components/common/Text/Text';

type LazyRelatedProductsProps = {
  brand: string;
  subscriptionLvl: string;
  currentProductId: string;
};

function LazyRelatedProducts({
  brand,
  subscriptionLvl,
  currentProductId
}: LazyRelatedProductsProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [products, setProducts] = useState<ProductCardData[] | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !fetchedRef.current) {
          fetchedRef.current = true;
          getRelatedProducts({ brand, subscriptionLvl, hitsCount: 10 })
            .then((result) => {
              const related = result.data.hits
                .map(algoliaResultToProductCard)
                .filter((item) => item.id !== currentProductId)
                .slice(0, 6);
              setProducts(related);
            })
            .catch(() => setProducts([]));
          observer.disconnect();
        }
      },
      { rootMargin: '400px' }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [brand, subscriptionLvl, currentProductId]);

  return (
    <div ref={ref}>
      {products && products.length > 0 && (
        <div className='shell-contained section-related-products'>
          <div className='related-headline'>
            <Text
              className='related-carousel-headline'
              variant='title-m'
              italic
            >
              You&apos;ll Also Love These Classics{' '}
              <Image
                className='related-headline-symbol'
                src={VSymbol}
                alt=''
                aria-hidden
                width={40}
                height={40}
              />
            </Text>
          </div>
          <div className='related-container'>
            <ProductCardCarousel
              className='related-products-desktop'
              data={products}
            />
            <div className='related-products-mobile'>
              {products.map((product, index) => {
                return (
                  <div
                    className={`related-products-mobile-item related-product-mobile-item-index-${index}`}
                    key={product.slug}
                  >
                    <ProductCard data={product} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LazyRelatedProducts;
